import type { ProjectDefinition } from "../../../types";
import {
  type CellValue,
  type FuncionProjectSpec,
  NOMBRES,
  conditions,
  excelDate,
  seededRandom,
} from "../spec";

const SHEET = "MD Reporte";
const TABLE = "PedidosMD";

const REGIONES = ["Norte", "Sur", "Centro", "Occidente"] as const;
// Plain first letters keep Excel's and JavaScript's alphabetical order identical.
const VENDEDORES = NOMBRES.slice(0, 7);
const PRODUCTOS = ["Laptop", "Monitor", "Impresora", "Servidor", "Tableta"] as const;

const rnd = seededRandom(9797);
// Fecha, Región, Vendedor, Producto, Importe
const ROWS = Array.from({ length: 60 }, () => [
  excelDate("2026-01-05") + rnd.int(0, 170),
  rnd.pick(REGIONES),
  rnd.pick(VENDEDORES),
  rnd.pick(PRODUCTOS),
  rnd.round(2000, 90000, 10),
] as [number, string, string, string, number]).sort((a, b) => a[0] - b[0]);

type Row = (typeof ROWS)[number];
const IN = { region: "Norte", minimo: 40000 };
const byText = (a: string, b: string) => a.localeCompare(b, "es", { sensitivity: "base" });

const lista = [...new Set(ROWS.map((r) => r[2]))].sort(byText);
const totalDe = (v: string) => ROWS.filter((r: Row) => r[2] === v).reduce((s, r) => s + r[4], 0);
const ranking = [...lista].sort((a, b) => totalDe(b) - totalDe(a));
const grandes = ROWS.filter((r) => r[1] === IN.region && r[4] >= IN.minimo).sort((a, b) => b[4] - a[4]);

const TOP = 10;
const BOTTOM = TOP + lista.length + 4;
const A = { lista: `L${TOP}`, numero: `K${TOP}`, total: `M${TOP}`, ranking: `O${TOP}`, grandes: `K${BOTTOM}` };

const col = (values: CellValue[]) => values.map((v) => [v]);
const EXPECTED: [string, string[], CellValue[][]][] = [
  [A.lista, ["SORT", "UNIQUE"], col(lista)],
  [A.numero, ["SEQUENCE", "ROWS|COUNTA"], col(lista.map((_, i) => i + 1))],
  [A.total, ["SUMIFS"], col(lista.map(totalDe))],
  [A.ranking, ["SORTBY"], col(ranking)],
  [A.grandes, ["SORT", "FILTER"], grandes],
];

export const spec: FuncionProjectSpec = {
  projectId: "funciones-reporte-matrices",
  sheets: [
    {
      name: SHEET,
      title: "Reporte automático",
      subtitle: "Pedidos del semestre — un reporte que crece solo cuando llegan vendedores o pedidos nuevos",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Fecha", "Región", "Vendedor", "Producto", "Importe"],
          rows: ROWS,
          formats: { Fecha: "dd/mm/yyyy", Importe: "$#,##0" },
        },
      ],
      inputs: [
        { address: "K4", label: "Región", value: IN.region },
        { address: "K5", label: "Importe mínimo", value: IN.minimo, format: "$#,##0" },
      ],
      answers: [
        { address: A.numero, label: "#", labelPosition: "above" },
        { address: A.lista, label: "Tarea 1 · Vendedores", labelPosition: "above" },
        {
          address: A.total,
          label: "Tarea 3 · Total",
          labelPosition: "above",
          areas: [{ range: `${A.total}:M${TOP + lista.length - 1}`, format: "$#,##0" }],
        },
        { address: A.ranking, label: "Tarea 4 · Ranking", labelPosition: "above" },
        {
          address: A.grandes,
          label: "Tarea 5 · Pedidos grandes de la región",
          labelPosition: "above",
          areas: [
            { range: `${A.grandes}:K${BOTTOM + grandes.length - 1}`, format: "dd/mm/yyyy" },
            { range: `O${BOTTOM}:O${BOTTOM + grandes.length - 1}`, format: "$#,##0" },
          ],
        },
      ],
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map(([address, fns, expected], i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address, fns, expected },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-reporte-matrices",
  title: "Reto final: Reporte automático",
  subtitle: "FILTRAR, UNICOS, ORDENAR, ORDENARPOR, SECUENCIA y #",
  description:
    "Tu jefe quiere un reporte que nunca haya que actualizar a mano: lista de vendedores, numeración, totales, ranking y los pedidos grandes de una región. Si mañana entra un vendedor nuevo, todo debe crecer solo.\n\nLa clave es el operador #: cada fórmula lee el resultado derramado de la anterior, así que forman una cadena.",
  topics: [
    "Una lista maestra con ORDENAR(UNICOS(...))",
    "Numeración automática con SECUENCIA(FILAS(...#))",
    "Totales alineados con #",
    "Ranking con ORDENARPOR sobre dos derrames",
    "FILTRAR con Y + ORDENAR",
  ],
  tasks: [
    {
      id: 1,
      level: "intermedio",
      title: "Tarea 1: Lista maestra de vendedores",
      description: `En la hoja <strong>MD Reporte</strong>, celda <strong>${A.lista}</strong>: muestra los <strong>Vendedores</strong> sin repetir y en orden alfabético. Esta lista será la base de las siguientes tareas.`,
      solution: [`Selecciona ${F(A.lista)}.`, `Escribe ${F("=ORDENAR(UNICOS(PedidosMD[Vendedor]))")}.`],
      expectedResult: "7 vendedores de la A a la Z",
      finalFormula: "=ORDENAR(UNICOS(PedidosMD[Vendedor]))",
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Numeración automática",
      description: `En <strong>${A.numero}</strong> (a la izquierda de la lista): numera los vendedores <strong>1, 2, 3…</strong>. La numeración debe tener <strong>tantas filas como la lista</strong> de ${A.lista}, aunque la lista crezca.`,
      solution: [
        `Selecciona ${F(A.numero)}.`,
        `${F(`${A.lista}#`)} es toda la lista derramada; ${F(`FILAS(${A.lista}#)`)} cuenta cuántos vendedores hay.`,
        `Escribe ${F(`=SECUENCIA(FILAS(${A.lista}#))`)}.`,
      ],
      expectedResult: "1 a 7",
      finalFormula: `=SECUENCIA(FILAS(${A.lista}#))`,
    },
    {
      id: 3,
      level: "reto",
      title: "Tarea 3: Total por vendedor",
      description: `En <strong>${A.total}</strong> (a la derecha de la lista): el <strong>Importe total</strong> de cada vendedor de la lista ${A.lista}. Usa <strong>#</strong> para que haya un total por cada nombre.`,
      solution: [
        `Selecciona ${F(A.total)}.`,
        `Escribe ${F(`=SUMAR.SI.CONJUNTO(PedidosMD[Importe],PedidosMD[Vendedor],${A.lista}#)`)}.`,
      ],
      expectedResult: "Un total por cada vendedor",
      finalFormula: `=SUMAR.SI.CONJUNTO(PedidosMD[Importe],PedidosMD[Vendedor],${A.lista}#)`,
    },
    {
      id: 4,
      level: "reto",
      title: "Tarea 4: Ranking de vendedores",
      description: `En <strong>${A.ranking}</strong>: muestra los nombres de la lista ${A.lista} ordenados por su <strong>total</strong> (${A.total}) de <strong>mayor a menor</strong>.`,
      solution: [
        `Selecciona ${F(A.ranking)}.`,
        "ORDENARPOR puede ordenar un derrame usando otro derrame del mismo tamaño como criterio.",
        `Escribe ${F(`=ORDENARPOR(${A.lista}#,${A.total}#,-1)`)}.`,
      ],
      expectedResult: "Del vendedor que más vendió al que menos",
      finalFormula: `=ORDENARPOR(${A.lista}#,${A.total}#,-1)`,
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Pedidos grandes de una región",
      description: conditions(`En <strong>${A.grandes}</strong>: muestra los pedidos completos que cumplan <strong>las dos</strong> condiciones, ordenados por <strong>Importe</strong> de mayor a menor:`, [
        "Región igual a la de <strong>K4</strong>",
        "Importe mayor o igual al de <strong>K5</strong>",
      ]),
      solution: [
        `Selecciona ${F(A.grandes)}.`,
        "FILTRAR con dos condiciones multiplicadas (Y) y, por fuera, ORDENAR por la columna 5 (Importe) descendente.",
        `Escribe ${F("=ORDENAR(FILTRAR(PedidosMD,(PedidosMD[Región]=K4)*(PedidosMD[Importe]>=K5)),5,-1)")}.`,
      ],
      expectedResult: "Pedidos del Norte de $40,000 o más, del mayor al menor",
      finalFormula: "=ORDENAR(FILTRAR(PedidosMD,(PedidosMD[Región]=K4)*(PedidosMD[Importe]>=K5)),5,-1)",
    },
  ],
};
